import { CalendarDays, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface WorkflowInstance {
  id: string;
  title: string;
  template_name: string;
  current_step: number;
  status: 'todo' | 'in_progress' | 'review' | 'done';
  due_date: string | null;
  step_count: number;
  completed_steps: number;
}

interface WorkflowCardProps {
  instance: WorkflowInstance;
  onClick: () => void;
}

const STATUS_COLORS: Record<WorkflowInstance['status'], string> = {
  todo: 'border-l-gray-400',
  in_progress: 'border-l-blue-500',
  review: 'border-l-amber-500',
  done: 'border-l-green-500',
};

export function WorkflowCard({ instance, onClick }: WorkflowCardProps) {
  const progress =
    instance.step_count > 0
      ? Math.round((instance.completed_steps / instance.step_count) * 100)
      : 0;

  const isOverdue =
    instance.due_date &&
    new Date(instance.due_date) < new Date() &&
    instance.status !== 'done';

  return (
    <Card
      onClick={onClick}
      className={`cursor-pointer border-l-4 transition-shadow hover:shadow-md ${STATUS_COLORS[instance.status]}`}
    >
      <CardContent className="space-y-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-sm font-semibold leading-tight line-clamp-2">
            {instance.title}
          </h4>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </div>

        <Badge variant="outline" className="text-xs">
          {instance.template_name}
        </Badge>

        <p className="text-xs text-muted-foreground">
          Step: {instance.current_step}
        </p>

        {/* Progress bar */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {instance.due_date && (
          <div className="flex items-center gap-1 text-xs">
            <CalendarDays className="h-3 w-3" />
            <span className={isOverdue ? 'font-medium text-destructive' : 'text-muted-foreground'}>
              {new Date(instance.due_date).toLocaleDateString()}
            </span>
            {isOverdue && (
              <Badge variant="destructive" className="ml-1 text-[10px] px-1 py-0">
                Overdue
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
